/**
 * 25SEVEN · Gift-with-purchase (GWP) für Horizon.
 *
 * Portiert aus Philips Prestige-Version (gleiche Logik, gleiche Snippets gwp-progress-bar / gwp-gift-picker):
 *  - Geschenkauswahl im Warenkorb (Drawer + /cart) per <gift-with-purchase>
 *  - Auto-Korrektur: Anzahl Geschenke nach Warenkorbwert (Stufe 2 = 1 Geschenk, Stufe 3 = 2), max. 1 Stück je Geschenk
 *  - Konfetti beim Überschreiten einer Stufe
 *  - Live-Aktualisierung des GWP-Balkens auf der Produktseite (<gwp-progress-live>)
 *  - Cross-Selling-Buttons im Warenkorb ([data-cart-add-variant])
 *
 * Unterschied zu Prestige: Horizon kommuniziert Warenkorb-Änderungen über das Standard-Event
 * „shopify:cart:lines-update" (CartLinesUpdateEvent aus @shopify/events) und morpht dann die Sections
 * aller <cart-items-component>. Eigene Änderungen lösen genau dieses Event aus, damit Drawer, Warenkorbseite,
 * Warenkorb-Zähler und Balken ohne Seiten-Reload aktuell bleiben.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const EVENT = CartLinesUpdateEvent.eventName || 'shopify:cart:lines-update';
const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

function readConfig() {
  const el = document.querySelector('.gwp-progress[data-gwp-tier2]');
  if (!el) return null;
  const tier1 = parseInt(el.dataset.gwpTier1 || '0', 10);
  const tier2 = parseInt(el.dataset.gwpTier2 || '0', 10);
  const tier3 = parseInt(el.dataset.gwpTier3 || '0', 10);
  if (!tier1 && !tier2 && !tier3) return null;
  return {
    tier1, tier2, tier3,
    giftProductIds: (el.dataset.gwpGiftProductIds || '').split(',').map((id) => parseInt(id.trim(), 10)).filter((id) => !Number.isNaN(id)),
  };
}
let configCache = readConfig();
const config = () => configCache || (configCache = readConfig());

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
}

async function fetchCart() {
  return (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
}

/**
 * Führt eine Warenkorb-Mutation aus und meldet sie Horizon (Morph von Drawer/Warenkorbseite, Zähler).
 */
async function mutate(url, body, action, sourceEl) {
  const sections = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  (sourceEl || document.documentElement).dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [{ merchandiseId: String(body.id), quantity: body.quantity }],
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(`${root()}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || `${url} failed`);
    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: json.sections, source: 'gwp-cart', didError: false, itemCount: cart.item_count },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* ---------------- Konfetti ---------------- */
let previousTotal = (() => {
  const el = document.querySelector('.gwp-progress[data-gwp-total]');
  return el ? parseInt(el.dataset.gwpTotal, 10) : null;
})();

function fireConfetti() {
  const colors = ['#9C5A28', '#A65A4B', '#1D1C17', '#F4F1EA', '#ffffff'];
  const container = document.createElement('div');
  container.className = 'gwp-confetti';
  container.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 60; i++) {
    const piece = document.createElement('span');
    const size = 6 + Math.random() * 6;
    piece.className = 'gwp-confetti__piece';
    Object.assign(piece.style, {
      left: `${Math.random() * 100}%`, width: `${size}px`, height: `${size * 0.4}px`,
      background: colors[Math.floor(Math.random() * colors.length)],
      animationDuration: `${2 + Math.random() * 1.5}s`, animationDelay: `${Math.random() * 0.3}s`,
      transform: `rotate(${Math.random() * 360}deg)`,
    });
    container.appendChild(piece);
  }
  // Top-Layer: Der Warenkorb-Drawer ist ein modaler <dialog> und liegt selbst im Top-Layer – dagegen hilft kein
  // z-index. Als Popover geöffnet liegt das Konfetti darüber (ältere Browser: fixed + z-index wie bisher).
  container.setAttribute('popover', 'manual');
  document.body.appendChild(container);
  try { container.showPopover(); } catch (e) { container.removeAttribute('popover'); }
  setTimeout(() => container.remove(), 3500);
}

function maybeFireConfetti(cart) {
  const c = config();
  if (!c || !cart) return;
  const wasBelow = (tier) => tier > 0 && previousTotal !== null && previousTotal < tier;
  const crossedTier3 = wasBelow(c.tier3) && cart.total_price >= c.tier3;
  const crossedOther = [c.tier1, c.tier2].some((t) => wasBelow(t) && cart.total_price >= t);
  previousTotal = cart.total_price;
  if (crossedTier3) { fireConfetti(); setTimeout(fireConfetti, 350); }
  else if (crossedOther) fireConfetti();
}

/* ---------------- Auto-Korrektur ---------------- */
let busy = false;
async function runAutoCorrect(cart) {
  const c = config();
  if (busy || !cart || !c || !c.tier2 || !c.tier3 || c.giftProductIds.length === 0) return;
  const maxGifts = cart.total_price >= c.tier3 ? 2 : cart.total_price >= c.tier2 ? 1 : 0;
  const giftLines = cart.items.filter((item) => c.giftProductIds.includes(item.product_id));
  const excess = giftLines.slice(maxGifts);
  const overQty = giftLines.slice(0, maxGifts).filter((line) => line.quantity > 1);
  if (!excess.length && !overQty.length) return;
  busy = true;
  try {
    for (const line of excess) await mutate('cart/change.js', { id: line.key, quantity: 0 }, 'remove');
    for (const line of overQty) await mutate('cart/change.js', { id: line.key, quantity: 1 }, 'update');
  } catch (e) {
    console.error('[GWP] Auto-Korrektur fehlgeschlagen', e.message);
  } finally {
    busy = false;
  }
}

/* ---------------- Custom Elements ---------------- */
class GiftWithPurchase extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-gwp-action]');
      if (!button || button.disabled || busy) return;
      busy = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const id = button.dataset.gwpVariantId;
        if (button.dataset.gwpAction === 'add') await mutate('cart/add.js', { id, quantity: 1 }, 'add', this);
        else await mutate('cart/change.js', { id, quantity: 0 }, 'remove', this);
      } catch (e) {
        console.error('[GWP]', e.message);
      } finally {
        busy = false;
        button.removeAttribute('aria-busy');
      }
    });
  }
}
if (!customElements.get('gift-with-purchase')) customElements.define('gift-with-purchase', GiftWithPurchase);

class GwpProgressLive extends HTMLElement {
  async refresh() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    const token = (this._latest = {});
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    let html;
    try { html = await (await fetch(url)).text(); } catch (e) { return; }
    if (this._latest !== token) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('.gwp-progress');
    if (fresh) this.innerHTML = fresh.outerHTML;
  }
}
if (!customElements.get('gwp-progress-live')) customElements.define('gwp-progress-live', GwpProgressLive);

/* Cross-Selling-Buttons im Warenkorb */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cart-add-variant]');
  if (!button || button.disabled) return;
  button.disabled = true;
  try { await mutate('cart/add.js', { id: button.dataset.cartAddVariant, quantity: 1 }, 'add', button); }
  catch (e) { console.error('[Cross-Sell]', e.message); button.disabled = false; }
});

/* ---------------- Quick Add der Produktkarten (snippets/pc-product-card.liquid) ----------------
 * Die Prestige-Karte rendert <product-form> + <quick-buy-modal>. Beide Elemente hatten in Horizon kein Script:
 * Das Formular lief nativ nach /cart (Drawer ging nie auf, Upsell fehlte) und der Button für Produkte mit
 * mehreren Varianten reagierte gar nicht. Jetzt: Hinzufügen über denselben Horizon-Fluss wie oben
 * (CartLinesUpdateEvent → Drawer öffnet sich automatisch, Drawer/Zähler/Balken werden gemorpht).
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('submit', async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        if (button?.getAttribute('aria-busy') === 'true') return;
        const data = new FormData(form);
        button?.setAttribute('aria-busy', 'true');
        try {
          await mutate('cart/add.js', { id: data.get('id'), quantity: parseInt(data.get('quantity') || '1', 10) }, 'add', this);
        } catch (e) {
          console.error('[Quick-Add]', e.message);
        } finally {
          button?.removeAttribute('aria-busy');
        }
      });
    }
  });
}

// Mehrere Varianten / Abos: Auswahl auf der Produktseite (das Prestige-Modal gibt es in Horizon nicht)
if (!customElements.get('quick-buy-modal')) customElements.define('quick-buy-modal', class extends HTMLElement {});
document.addEventListener('click', (event) => {
  const button = event.target.closest('button[aria-controls]');
  if (!button) return;
  const modal = document.getElementById(button.getAttribute('aria-controls'));
  if (!modal || modal.localName !== 'quick-buy-modal') return;
  event.preventDefault();
  window.location.href = `${root()}products/${modal.getAttribute('handle')}`;
});

/* ---------------- Upsell „ähnliche Produkte" im Warenkorb (snippets/cart-cross-sell.liquid) ---------------- */
const recommendationCache = new Map();

/** Empfehlungen (intent=related) für ein Produkt, als Liste von Karten-Wrappern [data-cart-rec-product-id] */
function fetchRecommendations(productId) {
  const url = `${root()}recommendations/products?product_id=${productId}&limit=10&intent=related&section_id=cart-recommendations`;
  if (!recommendationCache.has(url)) {
    recommendationCache.set(
      url,
      fetch(url)
        .then((res) => (res.ok ? res.text() : ''))
        .then((html) => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-cart-rec-product-id]')])
        .catch(() => {
          recommendationCache.delete(url);
          return [];
        })
    );
  }
  return recommendationCache.get(url);
}

class CartRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-ids', 'data-exclude-ids'];

  connectedCallback() {
    this.load();
  }

  // Horizons Morph aktualisiert die Attribute, lässt den Inhalt aber stehen (data-skip-subtree-update).
  // Der Morph setzt die Attribute nacheinander → erst laden, wenn alle neu sind (Microtask).
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.isConnected || this._queued) return;
    this._queued = true;
    queueMicrotask(() => {
      this._queued = false;
      this.load();
    });
  }

  async load() {
    const token = (this._token = {});
    const seeds = (this.dataset.productIds || '').split(',').filter(Boolean).slice(0, 3);
    const exclude = new Set([...(this.dataset.excludeIds || '').split(','), ...seeds].filter(Boolean));
    const limit = parseInt(this.dataset.limit || '3', 10);
    const list = this.querySelector('.complementary-products__product-list');
    if (!list) return;

    const results = await Promise.all(seeds.map(fetchRecommendations));
    if (this._token !== token) return;

    // Reihum aus den Empfehlungen je Warenkorb-Produkt, ohne Dubletten und ohne Produkte im Warenkorb
    const picked = [];
    const seen = new Set();
    const longest = Math.max(0, ...results.map((r) => r.length));
    for (let i = 0; i < longest && picked.length < limit; i++) {
      for (const cards of results) {
        const card = cards[i];
        const id = card?.dataset.cartRecProductId;
        if (!id || seen.has(id) || exclude.has(id)) continue;
        seen.add(id);
        picked.push(card);
        if (picked.length >= limit) break;
      }
    }

    if (picked.length) {
      list.replaceChildren(...picked.map((card) => document.importNode(card, true)));
    } else {
      // keine Empfehlungen → feste Liste bleibt, nur was inzwischen im Warenkorb liegt, fliegt raus
      list.querySelectorAll('[data-cart-rec-product-id]').forEach((card) => {
        if (exclude.has(card.dataset.cartRecProductId)) card.remove();
      });
    }
  }
}
if (!customElements.get('cart-recommendations')) customElements.define('cart-recommendations', CartRecommendations);

/* Jede Warenkorb-Änderung (auch Horizon-eigene): Konfetti, Auto-Korrektur, PDP-Balken */
document.addEventListener(EVENT, (event) => {
  const p = event.promise;
  if (!p || typeof p.then !== 'function') return;
  p.then(async ({ detail } = {}) => {
    if (detail && detail.source === 'gwp-cart' && busy) {
      // eigene Mutation: Balken & Konfetti trotzdem aktualisieren
    }
    const cart = await fetchCart();
    maybeFireConfetti(cart);
    document.querySelectorAll('gwp-progress-live').forEach((el) => el.refresh());
    if (!(detail && detail.source === 'gwp-cart')) runAutoCorrect(cart);
    else setTimeout(() => runAutoCorrect(cart), 0);
  }).catch(() => {});
});

/* Einmal beim Laden: Drift (z. B. nach Mengenänderung in einem anderen Tab) korrigieren */
if (config()) fetchCart().then(runAutoCorrect).catch(() => {});

/* Rabattcode im Drawer (Prestige-Optik): Codes über /cart/update.js setzen, Horizon-Sections neu rendern */
async function applyDiscountCodes(codes, sourceEl) {
  const cart = await mutate('cart/update.js', { discount: codes.filter(Boolean).join(',') }, 'update', sourceEl);
  return cart;
}
document.addEventListener('keydown', async (event) => {
  const input = event.target.closest && event.target.closest('pc-cart-discount input[name="pc_discount_code"]');
  if (!input || event.key !== 'Enter') return;
  event.preventDefault();
  const wrap = input.closest('pc-cart-discount');
  const code = input.value.trim(); if (!code) return;
  const existing = (wrap.querySelector('input[name="discount"]')?.value || '').split(',').map((c) => c.trim()).filter(Boolean);
  if (existing.includes(code)) return;
  wrap.classList.add('is-loading');
  try {
    const res = await fetch(`${root()}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: [...existing, code].join(',') }) });
    const json = await res.json();
    const ok = (json.discount_codes || []).some((d) => d.code.toLowerCase() === code.toLowerCase() && d.applicable);
    if (!ok) {
      await fetch(`${root()}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: existing.join(',') }) });
      const err = wrap.parentElement.querySelector('[data-pc-discount-error]'); if (err) err.hidden = false;
      return;
    }
    await applyDiscountCodes([...existing, code], wrap);
  } catch (e) { console.error('[Rabattcode]', e); }
  finally { wrap.classList.remove('is-loading'); }
});
document.addEventListener('click', async (event) => {
  const btn = event.target.closest('[data-pc-discount-remove]');
  if (!btn) return;
  const form = btn.closest('form');
  const existing = (form?.querySelector('input[name="discount"]')?.value || '').split(',').map((c) => c.trim()).filter(Boolean);
  try { await applyDiscountCodes(existing.filter((c) => c !== btn.dataset.pcDiscountRemove), btn); } catch (e) { console.error('[Rabattcode]', e); }
});
